import React, { useState } from 'react';
import {
  Stack,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { IconLibrary } from '@product-framework/index';
import formatTimeZoneDateTime from '@product-framework/utils/formatTimeZoneDateTime';
import { useNavigate } from 'react-router-dom';
import {
  useLocalization,
  SnackbarApiErrorMessage,
  getFormattedFileSize,
} from '@ui-framework/index';

function humanizeEnum(value) {
  if (value == null || value === '') return '';
  return String(value)
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function isPendingBatch(original) {
  return original?.batchStatus === 'PENDING';
}

function fileName(original) {
  return original?.fileName || '';
}

function createdDateTime(original) {
  const { formatMessage } = useLocalization();

  const { localDatetime, systemDatetime } = formatTimeZoneDateTime(
    original?.createdDateTime,
  );

  return (
    <Stack component="span" spacing={0.25} sx={{ py: 0.25 }}>
      <Typography component="span" variant="body2" sx={{ display: 'block' }}>
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{ mr: 0.75 }}>
          {formatMessage({
            id: 'YOUR_TIME',
            defaultMessage: 'Your Time',
          })}
          :
        </Typography>
        {localDatetime}
      </Typography>
      <Typography component="span" variant="body2" sx={{ display: 'block' }}>
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{ mr: 0.75 }}>
          {formatMessage({
            id: 'SYSTEM_TIME',
            defaultMessage: 'System Time',
          })}
          :
        </Typography>
        {systemDatetime}
      </Typography>
    </Stack>
  );
}

function createdByUser(original) {
  const user = original?.createdByUser;
  if (!user) return '';
  return [user.firstName, user.lastName].filter(Boolean).join(' ');
}

function fileSize(original) {
  if (isPendingBatch(original)) return '';
  const size = original?.fileSize;
  if (size == null || size === '') return '';
  return getFormattedFileSize(size);
}

function batchStatus(original, _props, accessorFunctionsAdditional) {
  const value = original?.batchStatus;
  if (value == null || value === '') return '';
  const { statusOptions = [] } = accessorFunctionsAdditional;
  const found = statusOptions.find(opt => opt.value === value);
  return found?.label || humanizeEnum(value);
}

function numberOfRecords(original) {
  if (isPendingBatch(original)) return '';
  const value = original?.numberOfRecords;
  if (value == null) return '';
  return value;
}

function ClaimPaymentImportDownloadButton({
  row,
  dataProvider,
  getDownloadUrl,
  getPresignedUrl,
  formatMessage,
  snackbar,
}) {
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    setLoading(true);
    try {
      const response = await getDownloadUrl(dataProvider, row?.id);
      if (response?.ok) {
        const presignedUrl = getPresignedUrl(response);
        if (presignedUrl) {
          window.open(presignedUrl, '_blank', 'noopener,noreferrer');
          setLoading(false);
          return;
        }
      }
      snackbar.showMessage({
        type: 'error',
        data: (
          <SnackbarApiErrorMessage
            errors={
              response?.errors || [
                {
                  message: formatMessage({
                    id: 'FAILED_TO_DOWNLOAD_IMPORT_FILE',
                    defaultMessage: 'Failed to download import file',
                  }),
                },
              ]
            }
          />
        ),
      });
    } catch (err) {
      snackbar.showMessage({
        type: 'error',
        data: (
          <SnackbarApiErrorMessage
            errors={[
              {
                message:
                  err?.message ||
                  formatMessage({
                    id: 'FAILED_TO_DOWNLOAD_IMPORT_FILE',
                    defaultMessage: 'Failed to download import file',
                  }),
              },
            ]}
          />
        ),
      });
    }
    setLoading(false);
  };

  return (
    <Tooltip
      title={formatMessage({
        id: 'DOWNLOAD_IMPORT_FILE',
        defaultMessage: 'Download import file',
      })}>
      <IconButton size="small" onClick={handleDownload} disabled={loading}>
        {loading ? (
          <CircularProgress color="inherit" size={18} />
        ) : (
          <IconLibrary.FileDownload />
        )}
      </IconButton>
    </Tooltip>
  );
}

function download(original, _props, accessorFunctionsAdditional) {
  if (!original?.id || !(Number(original?.fileSize) > 0)) {
    return null;
  }

  const {
    dataProvider,
    getDownloadUrl,
    getPresignedUrl,
    formatMessage,
    snackbar,
  } = accessorFunctionsAdditional;

  if (typeof getDownloadUrl !== 'function') {
    return null;
  }

  return (
    <ClaimPaymentImportDownloadButton
      row={original}
      dataProvider={dataProvider}
      getDownloadUrl={getDownloadUrl}
      getPresignedUrl={getPresignedUrl}
      formatMessage={formatMessage}
      snackbar={snackbar}
    />
  );
}

function ClaimPaymentImportPreviewButton({ row, formatMessage }) {
  const navigate = useNavigate();

  const handlePreview = () => {
    navigate(`/claim-payment-import/details/${row.id}`);
  };

  return (
    <Tooltip
      title={formatMessage({
        id: 'PREVIEW_IMPORT_DETAILS',
        defaultMessage: 'Preview import details',
      })}>
      <IconButton size="small" onClick={handlePreview}>
        <IconLibrary.View />
      </IconButton>
    </Tooltip>
  );
}

function preview(original, _props, accessorFunctionsAdditional) {
  if (!original?.id || isPendingBatch(original)) {
    return null;
  }

  const { formatMessage } = accessorFunctionsAdditional;

  return (
    <ClaimPaymentImportPreviewButton
      row={original}
      formatMessage={formatMessage}
    />
  );
}

export {
  download,
  preview,
  createdDateTime,
  createdByUser,
  fileName,
  fileSize,
  batchStatus,
  numberOfRecords,
};
